// src/pages/Equipe.jsx — Annuaire équipe + profil détaillé
import React, { useState, useEffect, useCallback } from 'react';
import api, { mesDocumentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Users, Search, MapPin, RefreshCw, ArrowLeft,
  Mail, Phone, Briefcase, Timer, ReceiptText, CalendarX,
  Activity, Folders, ChevronRight, FileText,
  Clock, CheckCircle2, XCircle, AlertCircle, Eye,
  UserCircle, Shield,
} from 'lucide-react';

const ROLE_CFG = {
  SUPER_ADMIN:        { label: 'Super Admin',          color: '#7C3AED' },
  ADMIN:              { label: 'Business Manager',       color: '#DC2626' },
  BUSINESS_MANAGER:   { label: "Ingénieur d'Affaires",  color: '#3B82F6' },
  INGENIEUR_AFFAIRES: { label: "Ingénieur d'Affaires",  color: '#3B82F6' },
  REFERENT_TECHNIQUE: { label: 'Référent Technique',    color: '#0891B2' },
  TALENT_ACQUISITION: { label: 'Talent Acquisition',    color: '#EA580C' },
  RH:                 { label: 'Ressources Humaines',   color: '#059669' },
  COLLABORATEUR:      { label: 'Collaborateur',          color: '#0891B2' },
};

const SITE_CFG = {
  FRANCE:   { label: 'France',   flag: '🇫🇷' },
  BELGIQUE: { label: 'Belgique', flag: '🇧🇪' },
  ALGERIE:  { label: 'Algérie',  flag: '🇩🇿' },
  TUNISIE:  { label: 'Tunisie',  flag: '🇹🇳' },
};

const DOC_TYPE_LABELS = {
  PIECE_IDENTITE: "Pièce d'identité", SECURITE_SOCIALE: 'Sécurité sociale',
  CONTRAT_TRAVAIL: 'Contrat', AVENANT: 'Avenant', RIB: 'RIB',
  MUTUELLE: 'Mutuelle', PERMIS_TRAVAIL: 'Permis', ATTESTATION: 'Attestation', AUTRE: 'Autre',
};

const MOIS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')} ${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function StatBadge({ value, label, color }) {
  return (
    <div style={{ textAlign:'center', padding:'10px 14px', borderRadius:10,
                  background:`${color}0d`, border:`1px solid ${color}22` }}>
      <div style={{ fontSize:'1.6rem', fontWeight:900, color, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:'0.67rem', fontWeight:600, color, textTransform:'uppercase',
                    letterSpacing:'0.04em', marginTop:4 }}>{label}</div>
    </div>
  );
}

// ── Onglets profil ────────────────────────────────────────────
const PROFILE_TABS = [
  { key:'info',    label:'Informations', icon: UserCircle },
  { key:'ts',      label:'Feuilles',     icon: Timer },
  { key:'nf',      label:'Frais',        icon: ReceiptText },
  { key:'abs',     label:'Congés',       icon: CalendarX },
  { key:'arrets',  label:'Arrêts',       icon: Activity },
  { key:'docs',    label:'Documents',    icon: Folders },
];

const STATUT_CFG = {
  SOUMIS:    { label:'En attente', color:'#0891B2', icon: Clock },
  BROUILLON: { label:'Brouillon',  color:'#8B8FA8', icon: Clock },
  VALIDE:    { label:'Validé',     color:'#059669', icon: CheckCircle2 },
  REJETE:    { label:'Rejeté',     color:'#DC2626', icon: XCircle },
  REFUSE:    { label:'Refusé',     color:'#DC2626', icon: XCircle },
  VALIDE_RH: { label:'Validé',     color:'#059669', icon: CheckCircle2 },
};

function StatusPill({ statut }) {
  const s = STATUT_CFG[statut] || { label: statut || '—', color:'#8B8FA8', icon: AlertCircle };
  const Ic = s.icon;
  return (
    <span style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4,
                   padding:'2px 8px', borderRadius:20, fontSize:'0.67rem', fontWeight:700,
                   background:`${s.color}14`, color:s.color }}>
      <Ic size={9}/>{s.label}
    </span>
  );
}

// ── Profil complet ────────────────────────────────────────────
function MemberProfile({ member, onBack, canSeeDetails }) {
  const role   = ROLE_CFG[(member.role || '').toUpperCase()] || { label: member.role || '—', color:'#8B8FA8' };
  const site   = SITE_CFG[(member.site || '').toUpperCase()];
  const initls = `${(member.prenom||'')[0]||''}${(member.nom||'')[0]||''}`.toUpperCase();

  const [tab,     setTab]     = useState('info');
  const [data,    setData]    = useState({});
  const [loading, setLoading] = useState({});

  const load = useCallback(async (key) => {
    if (data[key] || loading[key]) return;
    setLoading(p => ({ ...p, [key]: true }));
    try {
      let result = [];
      const uid = member.id;
      if (key === 'ts')     result = (await api.get('/validations/timesheets', { params: { user_id: uid, statut: 'SOUMIS' } })).data;
      if (key === 'nf')     result = (await api.get('/notes-frais/', { params: { user_id: uid } })).data;
      if (key === 'abs')    result = (await api.get('/absences/', { params: { user_id: uid } })).data;
      if (key === 'arrets') result = (await api.get('/arrets-maladie/', { params: { user_id: uid } })).data;
      if (key === 'docs')   result = (await mesDocumentsAPI.list({ user_id: uid })).data;
      setData(p => ({ ...p, [key]: result || [] }));
    } catch {
      setData(p => ({ ...p, [key]: [] }));
    } finally {
      setLoading(p => ({ ...p, [key]: false }));
    }
  }, [member.id, data, loading]);

  const switchTab = (key) => {
    setTab(key);
    if (key !== 'info' && canSeeDetails) load(key);
  };

  const visibleTabs = canSeeDetails ? PROFILE_TABS : [PROFILE_TABS[0]];

  return (
    <div style={{ minHeight:'100vh' }}>
      {/* Hero */}
      <div style={{ background:'linear-gradient(135deg, #0c1445 0%, #1a237e 60%, #283593 100%)',
                    padding:'28px 36px 24px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%',
                      background:'rgba(255,255,255,0.03)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-30, right:100, width:120, height:120, borderRadius:'50%',
                      background:'rgba(255,255,255,0.02)', pointerEvents:'none' }}/>

        <button onClick={onBack} style={{
          display:'flex', alignItems:'center', gap:6, padding:'7px 13px', marginBottom:18,
          borderRadius:8, border:'1px solid rgba(255,255,255,0.18)',
          background:'rgba(255,255,255,0.08)', cursor:'pointer',
          color:'rgba(255,255,255,0.75)', fontSize:'0.78rem', fontWeight:600,
        }}>
          <ArrowLeft size={13}/> Retour à l'équipe
        </button>

        <div style={{ display:'flex', alignItems:'center', gap:18 }}>
          <div style={{ width:64, height:64, borderRadius:'50%', flexShrink:0,
                        background:`${role.color}28`, border:`2px solid ${role.color}60`,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontWeight:900, fontSize:'1.4rem', color:'white' }}>
            {initls || '?'}
          </div>
          <div>
            <div style={{ fontSize:'1.6rem', fontWeight:800, color:'white', lineHeight:1.1 }}>
              {member.prenom} {member.nom}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
              <span style={{ whiteSpace:'nowrap', padding:'3px 10px', borderRadius:20, fontSize:'0.7rem', fontWeight:700,
                             background:`${role.color}28`, color:role.color,
                             border:`1px solid ${role.color}40` }}>
                {role.label}
              </span>
              {site && (
                <span style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.6)', display:'flex', alignItems:'center', gap:4 }}>
                  {site.flag} {site.label}
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:16, marginTop:8 }}>
              {member.email && (
                <a href={`mailto:${member.email}`} style={{ display:'flex', alignItems:'center', gap:5,
                  fontSize:'0.78rem', color:'rgba(255,255,255,0.55)', textDecoration:'none' }}>
                  <Mail size={12}/>{member.email}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-card)',
                    padding:'0 36px', display:'flex', gap:2 }}>
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            style={{
              display:'flex', alignItems:'center', gap:6,
              padding:'12px 14px', border:'none', cursor:'pointer',
              background:'transparent', fontWeight: tab===t.key ? 700 : 500,
              fontSize:'0.82rem',
              color: tab===t.key ? '#3B82F6' : 'var(--text-muted)',
              borderBottom: tab===t.key ? '2px solid #3B82F6' : '2px solid transparent',
              marginBottom:'-1px', transition:'color 0.12s',
            }}
          >
            <t.icon size={14}/>{t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:'28px 36px' }}>
        {/* Info */}
        {tab === 'info' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, maxWidth:600 }}>
            {[
              { icon: UserCircle, label:'Prénom', value: member.prenom },
              { icon: UserCircle, label:'Nom',    value: member.nom },
              { icon: Shield,     label:'Rôle',   value: role.label },
              { icon: MapPin,     label:'Site',   value: site ? `${site.flag} ${site.label}` : member.site },
              { icon: Mail,       label:'Email',  value: member.email },
              { icon: Briefcase,  label:'Poste',  value: member.poste },
            ].filter(r => r.value).map((r, i) => (
              <div key={i} style={{ padding:'14px 16px', borderRadius:11, background:'var(--bg-card)',
                                    border:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <r.icon size={14} color="#3B82F6"/>
                  <span style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--text-muted)',
                                  textTransform:'uppercase', letterSpacing:'0.04em' }}>{r.label}</span>
                </div>
                <div style={{ fontSize:'0.9rem', fontWeight:600, color:'var(--text-primary)' }}>{r.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Feuilles de temps */}
        {tab === 'ts' && canSeeDetails && (
          <TabContent loading={loading.ts} items={data.ts || []} empty="Aucune feuille en attente">
            {(data.ts || []).map((ts, i) => (
              <tr key={ts.id} style={{ borderBottom: i < (data.ts||[]).length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdS}>{String(ts.mois||'?').padStart(2,'0')}/{ts.annee||'?'}</td>
                <td style={tdS}><StatusPill statut={ts.statut}/></td>
              </tr>
            ))}
          </TabContent>
        )}

        {/* Notes de frais */}
        {tab === 'nf' && canSeeDetails && (
          <TabContent loading={loading.nf} items={data.nf || []} empty="Aucune note de frais">
            {(data.nf || []).map((n, i) => (
              <tr key={n.id} style={{ borderBottom: i < (data.nf||[]).length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdS}>{n.designation || '—'}</td>
                <td style={tdS}>{n.montant_ttc ? `${parseFloat(n.montant_ttc).toFixed(2)} €` : '—'}</td>
                <td style={tdS}>{fmtDate(n.date_effet || n.created_at)}</td>
                <td style={tdS}><StatusPill statut={n.statut}/></td>
              </tr>
            ))}
          </TabContent>
        )}

        {/* Congés */}
        {tab === 'abs' && canSeeDetails && (
          <TabContent loading={loading.abs} items={data.abs || []} empty="Aucune demande de congé">
            {(data.abs || []).map((a, i) => (
              <tr key={a.id} style={{ borderBottom: i < (data.abs||[]).length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdS}>{String(a.type_conge||'').replace(/_/g,' ')}</td>
                <td style={tdS}>{fmtDate(a.date_debut)} → {fmtDate(a.date_fin)}</td>
                <td style={tdS}>{a.nb_jours ? `${a.nb_jours}j` : '—'}</td>
                <td style={tdS}><StatusPill statut={a.statut}/></td>
              </tr>
            ))}
          </TabContent>
        )}

        {/* Arrêts maladie */}
        {tab === 'arrets' && canSeeDetails && (
          <TabContent loading={loading.arrets} items={data.arrets || []} empty="Aucun arrêt maladie">
            {(data.arrets || []).map((a, i) => (
              <tr key={a.id} style={{ borderBottom: i < (data.arrets||[]).length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdS}>{fmtDate(a.date_debut)} → {fmtDate(a.date_fin)}</td>
                <td style={tdS}>{a.nb_jours ? `${a.nb_jours}j` : '—'}</td>
                <td style={tdS}><StatusPill statut={a.statut}/></td>
              </tr>
            ))}
          </TabContent>
        )}

        {/* Documents */}
        {tab === 'docs' && canSeeDetails && (
          <TabContent loading={loading.docs} items={data.docs || []} empty="Aucun document">
            {(data.docs || []).map((d, i) => (
              <tr key={d.id} style={{ borderBottom: i < (data.docs||[]).length-1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdS}>{DOC_TYPE_LABELS[d.document_type] || d.document_type}</td>
                <td style={tdS}>{d.title || d.file_name || '—'}</td>
                <td style={tdS}>{fmtDate(d.created_at)}</td>
                <td style={tdS}><StatusPill statut={d.statut}/></td>
                <td style={tdS}>
                  {d.file_url && (
                    <a href={d.file_url} target="_blank" rel="noreferrer"
                       style={{ display:'inline-flex', alignItems:'center', gap:4,
                                padding:'4px 9px', borderRadius:6, textDecoration:'none',
                                background:'var(--bg-elevated)', border:'1px solid var(--border)',
                                color:'var(--text-secondary)', fontSize:'0.72rem', fontWeight:600 }}>
                      <Eye size={10}/> Voir
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </TabContent>
        )}
      </div>
    </div>
  );
}

const tdS = { padding:'11px 16px', fontSize:'0.82rem', color:'var(--text-primary)' };

function TabContent({ loading, items, empty, children }) {
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', paddingTop:60 }}>
      <div className="spinner" style={{ width:28, height:28 }}/>
    </div>
  );
  if (!items || items.length === 0) return (
    <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-muted)' }}>
      <CheckCircle2 size={36} style={{ opacity:0.18, display:'block', margin:'0 auto 12px' }}/>
      <div style={{ fontWeight:600 }}>{empty}</div>
    </div>
  );
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:12,
                  background:'var(--bg-card)', overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ── Carte membre ──────────────────────────────────────────────
function MemberCard({ u, onSelect }) {
  const [hov, setHov] = useState(false);
  const role   = ROLE_CFG[(u.role || '').toUpperCase()] || { label: u.role || '—', color: '#8B8FA8' };
  const site   = SITE_CFG[(u.site || '').toUpperCase()];
  const initls = `${(u.prenom || '')[0]||''}${(u.nom || '')[0]||''}`.toUpperCase();

  return (
    <div
      onClick={() => onSelect(u)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', flexDirection:'column', alignItems:'center',
        padding:'22px 16px 18px', borderRadius:14,
        border:`1.5px solid ${hov ? role.color : 'var(--border)'}`,
        background:'var(--bg-card)',
        transition:'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        boxShadow: hov ? `0 8px 24px ${role.color}14` : 'none',
        transform: hov ? 'translateY(-2px)' : 'none',
        textAlign:'center', cursor:'pointer',
      }}
    >
      <div style={{ width:56, height:56, borderRadius:'50%',
                    background:`${role.color}14`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontWeight:800, fontSize:'1.1rem', color:role.color,
                    marginBottom:12, position:'relative',
                    border:`2px solid ${role.color}30` }}>
        {initls || '?'}
        {site && (
          <span style={{ position:'absolute', bottom:-4, right:-4,
                         fontSize:'0.875rem', lineHeight:1 }}>
            {site.flag}
          </span>
        )}
      </div>

      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)',
                    marginBottom:4, lineHeight:1.2 }}>
        {u.prenom} {u.nom}
      </div>

      <span style={{ whiteSpace:'nowrap', display:'inline-block', padding:'2px 10px', borderRadius:20,
                     fontSize:'0.67rem', fontWeight:700, textTransform:'uppercase',
                     letterSpacing:'0.03em', marginBottom:8,
                     background:`${role.color}10`, color:role.color }}>
        {role.label}
      </span>

      {u.poste && (
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:4 }}>
          {u.poste}
        </div>
      )}
      {site && (
        <div style={{ display:'flex', alignItems:'center', gap:4,
                      fontSize:'0.7rem', color:'var(--text-muted)' }}>
          <MapPin size={10}/>{site.label}
        </div>
      )}

      <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:4,
                    fontSize:'0.7rem', color:'var(--text-muted)',
                    opacity: hov ? 1 : 0, transition:'opacity 0.15s' }}>
        Voir le profil <ChevronRight size={11}/>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function Equipe() {
  const { user } = useAuth();
  const [members,        setMembers]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [siteF,          setSiteF]          = useState('');
  const [roleF,          setRoleF]          = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  const role     = (user?.role || '').toUpperCase();
  const canSeeDetails = ['SUPER_ADMIN','ADMIN','RH','BUSINESS_MANAGER','INGENIEUR_AFFAIRES'].includes(role);

  const load = () => {
    setLoading(true);
    api.get('/admin/equipe')
      .then(r => setMembers(r.data || []))
      .catch(() => toast.error('Erreur chargement équipe'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (selectedMember) {
    return (
      <MemberProfile
        member={selectedMember}
        onBack={() => setSelectedMember(null)}
        canSeeDetails={canSeeDetails}
      />
    );
  }

  const filtered = members.filter(m => {
    if (siteF && (m.site || '').toUpperCase() !== siteF) return false;
    if (roleF && (m.role || '').toUpperCase() !== roleF) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${m.prenom} ${m.nom} ${m.poste || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const sites = [...new Set(members.map(m => (m.site || '').toUpperCase()).filter(Boolean))];
  const roles = [...new Set(members.map(m => (m.role || '').toUpperCase()).filter(Boolean))];

  return (
    <div style={{ padding:'28px 36px', minHeight:'100vh' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <div style={{ width:36, height:36, borderRadius:10,
                          background:'rgba(8,145,178,0.10)',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Users size={18} color="#0891B2" />
            </div>
            <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:'var(--text-primary)' }}>
              Notre équipe
            </h1>
          </div>
          <p style={{ margin:'0 0 0 46px', color:'var(--text-muted)', fontSize:'0.85rem' }}>
            {filtered.length} membre{filtered.length !== 1 ? 's' : ''} actif{filtered.length !== 1 ? 's' : ''}
            {canSeeDetails && <span style={{ marginLeft:8, fontSize:'0.75rem', color:'#0891B2' }}>• Cliquez pour voir le profil complet</span>}
          </p>
        </div>
        <button onClick={load} style={{ display:'flex', alignItems:'center', gap:6,
                                        padding:'8px 14px', borderRadius:9, border:'1px solid var(--border)',
                                        background:'var(--bg-elevated)', cursor:'pointer',
                                        color:'var(--text-muted)', fontSize:'0.82rem' }}>
          <RefreshCw size={13}/> Rafraîchir
        </button>
      </div>

      {/* Filtres */}
      <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative' }}>
          <Search size={13} style={{ position:'absolute', left:10, top:'50%',
                                     transform:'translateY(-50%)', color:'var(--text-muted)',
                                     pointerEvents:'none' }}/>
          <input
            placeholder="Rechercher…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft:30, paddingRight:12, paddingTop:8, paddingBottom:8,
                     borderRadius:8, border:'1px solid var(--border)', outline:'none',
                     background:'var(--bg-elevated)', color:'var(--text-primary)',
                     fontSize:'0.82rem', width:200 }}
          />
        </div>

        {sites.length > 1 && (
          <select value={siteF} onChange={e => setSiteF(e.target.value)}
            style={{ padding:'8px 10px', borderRadius:8, border:'1px solid var(--border)',
                     background:'var(--bg-elevated)', color:'var(--text-primary)',
                     fontSize:'0.82rem', cursor:'pointer' }}>
            <option value="">Tous les sites</option>
            {sites.map(s => <option key={s} value={s}>{SITE_CFG[s]?.flag} {SITE_CFG[s]?.label || s}</option>)}
          </select>
        )}

        {roles.length > 1 && (
          <select value={roleF} onChange={e => setRoleF(e.target.value)}
            style={{ padding:'8px 10px', borderRadius:8, border:'1px solid var(--border)',
                     background:'var(--bg-elevated)', color:'var(--text-primary)',
                     fontSize:'0.82rem', cursor:'pointer' }}>
            <option value="">Tous les rôles</option>
            {roles.map(r => <option key={r} value={r}>{ROLE_CFG[r]?.label || r}</option>)}
          </select>
        )}
      </div>

      {/* Grille */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', paddingTop:80 }}>
          <div className="spinner" style={{ width:32, height:32 }}/>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 0', color:'var(--text-muted)' }}>
          <Users size={44} style={{ opacity:0.18, display:'block', margin:'0 auto 14px' }}/>
          <div style={{ fontWeight:600, marginBottom:6 }}>Aucun membre trouvé</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:14 }}>
          {filtered.map(m => <MemberCard key={m.id} u={m} onSelect={setSelectedMember} />)}
        </div>
      )}
    </div>
  );
}
